import { useState } from "react";
import CodeBadge from './CodeBadge';
import CodePicker from './CodePicker';
import HandoutModal from './HandoutModal';
import { claudePost, HANDOUT_SYSTEM } from '../utils/claudeApi';
import { fmt, duration } from '../utils/formatting';

export default function EventRow({ event, onUpdate, onSuggest, onDelete, onClearCodes }) {
  const [suggesting, setSuggesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [generatingHandout, setGeneratingHandout] = useState(false);
  const [handout, setHandout] = useState(null);

  const handleSuggest = async () => {
    setSuggesting(true);
    setError(null);
    try {
      const result = await onSuggest(event);
      onUpdate({ ...event, ...result });
    } catch (e) {
      setError("AI suggestion failed — check your API key in Settings");
    }
    setSuggesting(false);
  };

  const handleGenerateHandout = async () => {
    setGeneratingHandout(true);
    setError(null);
    try {
      const icd = (event.icd10 || []).join(", ") || "not specified";
      const cpt = (event.cpt || []).join(", ") || "not specified";
      const userPrompt = `Patient appointment: "${event.title}"
Date: ${fmt(event.start)}${event.location ? `\nLocation: ${event.location}` : ""}${event.notes ? `\nClinical notes: ${event.notes}` : ""}${event.dob ? `\nDOB: ${event.dob}` : ""}
ICD-10 diagnoses: ${icd}
CPT procedures: ${cpt}

Write a patient handout for this appointment following the required sections.`;
      const text = await claudePost(
        [{ role: "user", content: userPrompt }],
        600,
        HANDOUT_SYSTEM
      );
      setHandout(text);
    } catch (e) {
      setError("Handout generation failed — check your API key in Settings");
    }
    setGeneratingHandout(false);
  };

  return (
    <>
      {handout && <HandoutModal handout={handout} title={event.title} onClose={() => setHandout(null)} />}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "20px 24px", marginBottom: 12 }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "#f0ede8" }}>{event.title}</div>
              {((event.icd10?.length || 0) + (event.cpt?.length || 0) > 0) && (
                <span style={{ fontSize: 10, color: "#6ee7b7", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)", borderRadius: 20, padding: "2px 7px", fontWeight: 600, letterSpacing: "0.03em" }}>💾 Saved</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
              <span>📅 {fmt(event.start)}</span>
              {event.start && event.end && <span>⏱ {duration(event.start, event.end)}</span>}
              {event.location && <span>📍 {event.location}</span>}
            </div>
            {event.dob && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>DOB: {event.dob}</div>}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button onClick={() => setEditing(!editing)} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 11 }}>
              {editing ? "Done" : "Edit Notes"}
            </button>
            <button onClick={handleSuggest} disabled={suggesting} style={{ background: suggesting ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.3)", border: "1px solid rgba(99,102,241,0.5)", color: suggesting ? "#a5b4fc" : "#c7d2fe", borderRadius: 8, padding: "6px 14px", cursor: suggesting ? "wait" : "pointer", fontSize: 11 }}>
              {suggesting ? "✨ Suggesting…" : "✨ AI Suggest Codes"}
            </button>
            <button onClick={handleGenerateHandout} disabled={generatingHandout} style={{ background: generatingHandout ? "rgba(52,211,153,0.1)" : "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.35)", color: generatingHandout ? "rgba(52,211,153,0.5)" : "#6ee7b7", borderRadius: 8, padding: "6px 14px", cursor: generatingHandout ? "wait" : "pointer", fontSize: 11 }}>
              {generatingHandout ? "📄 Generating…" : "📄 Handout"}
            </button>
            {((event.icd10?.length || 0) + (event.cpt?.length || 0) > 0) && (
              <button onClick={() => onClearCodes(event.id)} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.35)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11 }} title="Clear saved codes">Clear</button>
            )}
            <button onClick={() => onDelete(event.id)} style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", color: "#f87171", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 13, lineHeight: 1 }} title="Delete event">×</button>
          </div>
        </div>
        {error && <div style={{ fontSize: 11, color: "#f87171", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 6, padding: "6px 12px", marginBottom: 12 }}>⚠ {error}</div>}
        {editing && (
          <div style={{ marginBottom: 14 }}>
            <textarea value={event.notes || ""} onChange={e => onUpdate({ ...event, notes: e.target.value })} placeholder="Add clinical notes…"
              style={{ width: "100%", minHeight: 60, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#f0ede8", fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            <input value={event.dob || ""} onChange={e => onUpdate({ ...event, dob: e.target.value })} placeholder="Date of Birth (YYYY-MM-DD)"
              style={{ marginTop: 8, padding: "6px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#f0ede8", fontSize: 12, outline: "none", width: 200 }} />
          </div>
        )}
        {!editing && event.notes && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 14, fontStyle: "italic", paddingLeft: 4 }}>"{event.notes}"</div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {(event.icd10 || []).map(c => <CodeBadge key={c} code={c} type="icd10" onRemove={code => onUpdate({ ...event, icd10: (event.icd10 || []).filter(x => x !== code) })} />)}
          <CodePicker type="icd10" selected={event.icd10 || []} onChange={codes => onUpdate({ ...event, icd10: codes })} />
          <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />
          {(event.cpt || []).map(c => <CodeBadge key={c} code={c} type="cpt" onRemove={code => onUpdate({ ...event, cpt: (event.cpt || []).filter(x => x !== code) })} />)}
          <CodePicker type="cpt" selected={event.cpt || []} onChange={codes => onUpdate({ ...event, cpt: codes })} />
        </div>
      </div>
    </>
  );
}
